import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Spin } from 'antd';
import './mcp.css';
import { authHeader } from '../../../utils/api';
import { useNavigate } from 'react-router-dom';
import McpHeader from '../../../components/mcp/McpHeader';
import McpMapLibre from '../../../components/mcp/map/McpMapLibre';
import { clusterStopsToHotZones, haversineMi } from '../../../components/mcp/map/mapHelpers';
import dayjs from 'dayjs';

const RADIUS_OPTIONS = [1, 2, 5];

function filterStops(stops, filter) {
  let out = stops;
  if (filter.hiddenAssetIds.length > 0) {
    const hidden = new Set(filter.hiddenAssetIds);
    out = out.filter(s => !hidden.has(s.truckId));
  }
  if (filter.ignitionOffOnly) {
    out = out.filter(s => s.ignitionOff);
  }
  const max = filter.maxMin;
  out = out.filter(s => s.durationMin >= filter.minMin && s.durationMin <= max);
  return out;
}

function dedupeStopsByLocation(stops, radiusMi) {
  const groups = [];
  for (const s of stops) {
    let placed = false;
    for (const g of groups) {
      const head = g[0];
      if (haversineMi(s.lat, s.lng, head.lat, head.lng) <= radiusMi) {
        g.push(s);
        placed = true;
        break;
      }
    }
    if (!placed) groups.push([s]);
  }
  return groups.map(g => g.sort((a, b) => b.durationMin - a.durationMin)[0]);
}

const McpMap = () => {
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [stops, setStops] = useState([]);
  const [hotZonesThreshold, setHotZonesThreshold] = useState({ epsMi: 1, minPoints: 2 });
  const [hotZonesActive, setHotZonesActive] = useState(false);
  const [stopFilter, setStopFilter] = useState({
    minMin: 0,
    maxMin: Number.POSITIVE_INFINITY,
    ignitionOffOnly: false,
    hiddenAssetIds: [],
    overlapRadiusMi: 0,
  });
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [mode, setMode] = useState('stop');
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectedHotZoneIds, setSelectedHotZoneIds] = useState([]);
  const [radiusMi, setRadiusMi] = useState(1);
  const [heatmap, setHeatmap] = useState('off');
  const [days, setDays] = useState(14);
  const [customDays, setCustomDays] = useState(365);
  const [fleetTotal, setFleetTotal] = useState(0);
  const [fleetActive, setFleetActive] = useState(0);
  const mapSectionRef = useRef(null);

  // Scroll the map into view when a Fleet Pulse card selects zones
  useEffect(() => {
    if (selectedHotZoneIds.length > 0 && mapSectionRef.current) {
      mapSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [selectedHotZoneIds]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    async function load() {
      try {
        const [fc, c, geotabAccounts, assignmentsData] = await Promise.all([
          fetch('/api/mcp/fleet-map', { headers: authHeader() })
            .then(r => r.ok ? r.json() : { features: [] })
            .catch(() => ({ features: [] })),
          fetch('/api/campaigns', { headers: authHeader() })
            .then(r => r.ok ? r.json() : [])
            .catch(() => []),
          fetch('/api/integrations/geotab/accounts', { headers: authHeader() })
            .then(r => r.ok ? r.json() : [])
            .catch(() => []),
          fetch('/api/client-vehicle-assignments/my-vehicles', { headers: authHeader() })
            .then(r => r.ok ? r.json() : [])
            .catch(() => []),
        ]);

        const assignments = Array.isArray(assignmentsData) ? assignmentsData : (assignmentsData.items || []);

        if (cancelled) return;

        const feats = fc?.features || [];
        const mappedVehicles = feats.map(f => ({
          id: f.properties.id,
          device_id: f.properties.deviceId,
          name: f.properties.name,
          licensePlate: f.properties.licensePlate,
          status: f.properties.status,
          is_active: f.properties.status === 'active',
          last_seen: f.properties.lastSeen,
          latitude: f.geometry.coordinates[1],
          longitude: f.geometry.coordinates[0],
        }));
        let deviceToVehicleId = Object.fromEntries(mappedVehicles.map(v => [v.device_id, v.id]));
        setVehicles(mappedVehicles);
        setFleetTotal(fc?.meta?.fleetTotal ?? mappedVehicles.length);
        setFleetActive(fc?.meta?.fleetActive ?? mappedVehicles.filter(v => v.is_active).length);
        setCampaigns(Array.isArray(c) ? c : c.items || c.campaigns || c.data || []);

        const vehicleStops = mappedVehicles
          .filter(v => Number(v.latitude) && Number(v.longitude))
          .map(v => ({
            id: `vehicle-${v.id}`,
            truckId: v.id,
            lat: Number(v.latitude),
            lng: Number(v.longitude),
            address: v.name || v.licensePlate || 'Vehicle',
            durationMin: 0,
          }));
        setStops(vehicleStops);
        if (!cancelled) setLoading(false);

        const accounts = Array.isArray(geotabAccounts) ? geotabAccounts : (geotabAccounts.items || []);
        const databases = accounts.map(a => a?.database).filter(Boolean).filter((x, i, a) => a.indexOf(x) === i);

        if (databases.length === 0) {
          setRoutes([]);
          // hot zones computed in render
          setLoading(false);
          return;
        }

        const devicesRes = await fetch(`/api/integrations/geotab/devices?databases=${encodeURIComponent(databases.join(','))}`, { headers: authHeader() });
        const devicesData = await (devicesRes.ok ? devicesRes.json() : { devices: [] });
        const devices = (devicesData.devices || []).filter(d => databases.includes(d.database));
        const deviceIds = devices.map(d => d.id);

        const assignedIds = new Set((assignments || []).map(a => a.geotab_device_id));
        const assignedDevices = devices.filter(d => assignedIds.has(d.id));
        const now = new Date();
        const mappedFromDevices = assignedDevices.map(d => {
          const activeTo = d.activeTo ? new Date(d.activeTo) : null;
          const isActive = !activeTo || activeTo > now || activeTo.getFullYear() >= 2030;
          return {
            id: d.id,
            device_id: d.id,
            name: d.name || d.description || `Vehicle ${d.id}`,
            licensePlate: d.licensePlate || '',
            status: isActive ? 'active' : 'inactive',
            is_active: isActive,
            last_seen: d.lastSeen || null,
            latitude: typeof d.latitude === 'number' ? d.latitude : null,
            longitude: typeof d.longitude === 'number' ? d.longitude : null,
          };
        });
        setFleetTotal(assignments.length);
        setFleetActive(mappedFromDevices.filter(v => v.is_active).length);
        if (mappedVehicles.length === 0) {
          setVehicles(mappedFromDevices);
          deviceToVehicleId = Object.fromEntries(mappedFromDevices.map(v => [v.device_id, v.id]));
          const fallbackStops = mappedFromDevices
            .filter(v => Number(v.latitude) && Number(v.longitude))
            .map(v => ({
              id: `vehicle-${v.id}`,
              truckId: v.id,
              lat: Number(v.latitude),
              lng: Number(v.longitude),
              address: v.name || v.licensePlate || 'Vehicle',
              durationMin: 0,
            }));
          setStops(fallbackStops);
          if (!cancelled) setLoading(false);
        }

        let trips = [];
        let gpsStops = [];
        const startDate = dayjs().subtract(days, 'day').startOf('day');
        const endDate = dayjs().endOf('day');

        // Fetch actual stops with addresses & durations from gps-addresses endpoint
        if (databases.length > 0) {
          try {
            const stopsParams = new URLSearchParams({
              databases: databases.join(','),
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              maxAddresses: 500,
            });
            if (deviceIds.length > 0) stopsParams.set('deviceIds', deviceIds.join(','));
            const stopsRes = await fetch(`/api/integrations/geotab/gps-addresses?${stopsParams}`, { headers: authHeader() });
            if (stopsRes.ok) {
              const stopsData = await stopsRes.json();
              gpsStops = stopsData.addresses || [];
              console.log('[McpMap] gps-addresses response:', gpsStops.length, 'stops, sample:', gpsStops[0]);
            } else {
              console.log('[McpMap] gps-addresses endpoint returned', stopsRes.status);
            }
          } catch {
            // ignore — fall back to trip endpoints
          }
        }

        // Fetch trips for routes
        if (deviceIds.length > 0) {
          const tripsRes = await fetch('/api/integrations/geotab/trips', {
            method: 'POST',
            headers: { ...authHeader(), 'Content-Type': 'application/json' },
            body: JSON.stringify({
              deviceIds: deviceIds.join(','),
              databases: databases.join(','),
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
            }),
          });
          const tripsData = await (tripsRes.ok ? tripsRes.json() : { trips: [] });
          trips = tripsData.trips || [];
        }

        const transformedRoutes = trips.map((trip, index) => {
          const coords = (trip.route || [])
            .filter(p => typeof p.latitude === 'number' && typeof p.longitude === 'number')
            .map(p => [p.longitude, p.latitude]);
          if (coords.length < 2) return null;
          return {
            id: trip.id || `trip-${index}`,
            truckId: deviceToVehicleId[trip.deviceId] || trip.deviceId || trip.device,
            coordinates: coords,
          };
        }).filter(Boolean);

        // Build stops from gps-addresses data (has proper addresses & durations)
        const realStops = gpsStops
          .filter(s => typeof s.latitude === 'number' && typeof s.longitude === 'number')
          .map((s, i) => {
            const vehicleName = s.device || s.deviceName || deviceToVehicleId[s.deviceId] || s.deviceId || 'Vehicle';
            const addrParts = [s.address, s.city, s.state].filter(Boolean);
            const addr = addrParts.length > 0 ? addrParts.join(', ') : vehicleName;
            return {
              id: `stop-${i}`,
              truckId: deviceToVehicleId[s.deviceId] || s.deviceId || s.device,
              lat: s.latitude,
              lng: s.longitude,
              address: addr,
              durationMin: s.totalDurationMinutes || s.durationMinutes || 0,
            };
          });

        setRoutes(transformedRoutes);
        // Use real stops if available, otherwise fall back to vehicle positions + trip endpoints
        if (realStops.length > 0) {
          console.log('[McpMap] Using real stops:', realStops.length, 'sample:', realStops[0]);
          setStops(realStops);
        } else {
          const tripStops = trips.reduce((acc, trip) => {
            const first = trip.route?.[0];
            const last = trip.route?.[trip.route.length - 1];
            const points = [
              { p: first, address: trip.startAddress, durationMin: 0 },
              { p: last, address: trip.endAddress, durationMin: trip.durationMinutes || 0 },
            ];
            for (const { p, address, durationMin } of points) {
              if (!p || typeof p.latitude !== 'number' || typeof p.longitude !== 'number') continue;
              const vehicleName = deviceToVehicleId[trip.deviceId] || trip.deviceId || trip.device || 'Vehicle';
              acc.push({
                id: `trip-stop-${acc.length}`,
                truckId: deviceToVehicleId[trip.deviceId] || trip.deviceId || trip.device,
                lat: p.latitude,
                lng: p.longitude,
                address: (address && address !== 'Unknown Location') ? address : vehicleName,
                durationMin,
              });
            }
            return acc;
          }, []);
          const allStops = [...vehicleStops, ...tripStops];
          setStops(allStops);
        }
        // hot zones computed in render
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [days]);

  const hotZones = useMemo(() =>
    clusterStopsToHotZones(stops, hotZonesThreshold.epsMi)
      .filter(z => (z.stopIds?.length || 0) >= hotZonesThreshold.minPoints),
  [stops, hotZonesThreshold]);

  const filteredStops = useMemo(() => {
    let out = filterStops(stops, stopFilter);
    if (stopFilter.overlapRadiusMi > 0) {
      out = dedupeStopsByLocation(out, stopFilter.overlapRadiusMi);
    }
    return out;
  }, [stops, stopFilter]);

  const visibleStops = useMemo(() => {
    if (selectedHotZoneIds.length === 0) return filteredStops;
    const selectedZones = hotZones.filter(z => selectedHotZoneIds.includes(z.id));
    const ids = new Set();
    for (const z of selectedZones) for (const id of z.stopIds) ids.add(id);
    return filteredStops.filter(s => ids.has(s.id));
  }, [filteredStops, hotZones, selectedHotZoneIds]);

  const visibleRoutes = useMemo(() => {
    if (selectedIds.length === 0) return routes;
    return routes.filter(r => selectedIds.includes(r.id) || selectedIds.includes(r.truckId));
  }, [routes, selectedIds]);

  const trucks = useMemo(() => vehicles.map(v => ({
    id: v.id,
    name: v.name || v.licensePlate || 'Vehicle',
    color: v.is_active ? '#1F4FE5' : '#8A93A7',
  })), [vehicles]);

  function toggleSelect(id) {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  function selectAll() {
    if (mode === 'stop') {
      setSelectedIds(stops.map(s => s.id));
    } else {
      setSelectedIds(routes.map(r => r.id));
    }
  }

  function handleHotZoneSelect(zoneId) {
    setSelectedHotZoneIds(prev => prev.includes(zoneId) ? prev.filter(id => id !== zoneId) : [...prev, zoneId]);
  }

  function handleHotZoneStopsSelected(zoneId) {
    const zone = hotZones.find(z => z.id === zoneId);
    if (!zone) return;
    const ids = zone.stopIds;
    setSelectedIds(prev => {
      const allSelected = ids.every(id => prev.includes(id));
      if (allSelected) return prev.filter(id => !ids.includes(id));
      const set = new Set(prev);
      for (const id of ids) set.add(id);
      return Array.from(set);
    });
  }

  const totalMiles = useMemo(() => {
    return Math.round(routes.reduce((sum, r) => {
      const c = r.coordinates || [];
      for (let i = 1; i < c.length; i++) {
        sum += haversineMi(c[i - 1][1], c[i - 1][0], c[i][1], c[i][0]);
      }
      return sum;
    }, 0));
  }, [routes]);

  const mapDataStops = mode === 'stop' ? visibleStops : [];
  const mapDataRoutes = mode === 'route' ? visibleRoutes : [];

  if (loading) {
    return (
      <div className="mcp-page p-6" style={{ textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" tip="Loading fleet data…" />
      </div>
    );
  }

  return (
    <div className="mcp-page" style={{ minHeight: '100vh', overflowY: 'auto' }}>
      <McpHeader title="Your fleet, your map" />
      {error && (
        <Alert
          message={error}
          type="error"
          banner
          closable
          onClose={() => setError(null)}
          style={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 1000 }}
        />
      )}

      <div className="mx-auto w-full max-w-7xl space-y-8 p-6 md:p-8">
        {/* Header strip */}
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-bold text-ink">Your service area, right now.</h2>
            <p className="mt-1 text-sm text-ink-200">
              {fleetActive} of {fleetTotal} {fleetTotal === 1 ? 'truck' : 'trucks'} active · {stops.length} {stops.length === 1 ? 'stop' : 'stops'} · {totalMiles} {totalMiles === 1 ? 'mile' : 'miles'} driven (last {days} {days === 1 ? 'day' : 'days'}).
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            {[
              { label: 'Trucks', value: `${fleetActive}/${fleetTotal}` },
              { label: 'Stops', value: `${stops.length}`, hide: stops.length === 0 },
              { label: 'Routes', value: `${routes.length}`, hide: routes.length === 0 },
              { label: 'Audiences', value: `${campaigns.length}` },
            ].filter(s => !s.hide).map((s) => (
              <div key={s.label} className="rounded-2xl border-2 border-paper-300 bg-paper-50 px-4 py-2.5 shadow-soft">
                <div className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">{s.label}</div>
                <div className="mt-0.5 font-display text-xl font-bold text-ink">{s.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Fleet pulse insights */}
        <section>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-200">
              Fleet Pulse — last {days} days on the truck
            </div>
            <div className="inline-flex items-center rounded-full border-2 border-ink/15 bg-paper p-0.5">
              {[7, 14, 30, 60, 90, 180].map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${days === d ? 'bg-ink text-paper' : 'text-ink-100 hover:bg-paper-200'}`}
                >
                  {d}d
                </button>
              ))}
              <input
                type="number"
                min={1}
                placeholder="Custom"
                value={![7, 14, 30, 60, 90, 180].includes(days) ? days : customDays}
                onChange={(e) => {
                  const n = Number(e.target.value);
                  if (n > 0) {
                    setCustomDays(n);
                    setDays(n);
                  }
                }}
                className={`w-20 rounded-full bg-transparent px-2 py-1 text-center text-xs font-semibold outline-none transition ${![7, 14, 30, 60, 90, 180].includes(days) ? 'bg-ink text-paper' : 'text-ink-100 hover:bg-paper-200'}`}
              />
            </div>
            <div className="ml-2 inline-flex rounded-full border-2 border-ink/15 bg-paper p-0.5">
              <button
                type="button"
                onClick={() => { const presets = [7, 14, 30, 60, 90, 180]; const i = presets.indexOf(days); setDays(i === -1 ? 7 : presets[(i + presets.length - 1) % presets.length]); }}
                className="rounded-full px-2 py-1 text-xs font-semibold text-ink-100 hover:bg-paper-200 transition"
                aria-label="Previous range"
              >
                ←
              </button>
              <button
                type="button"
                onClick={() => { const presets = [7, 14, 30, 60, 90, 180]; const i = presets.indexOf(days); setDays(i === -1 ? 7 : presets[(i + 1) % presets.length]); }}
                className="rounded-full px-2 py-1 text-xs font-semibold text-ink-100 hover:bg-paper-200 transition"
                aria-label="Next range"
              >
                →
              </button>
            </div>
          </div>

          <div className="mt-12 grid grid-cols-1 gap-x-6 gap-y-12 md:grid-cols-2 xl:grid-cols-4">
            {/* Campaign pulse */}
            <div className="relative h-full">
              <div className="absolute -top-3 -left-3 z-10 flex items-center gap-2">
                <div className="inline-flex items-center justify-center rounded-2xl bg-sun px-4 py-2 text-sm font-bold text-ink shadow-sun" style={{ transform: 'rotate(-4deg)' }}>
                  Live now
                </div>
              </div>
              <div className="relative h-full overflow-hidden rounded-3xl border border-paper-300/60 bg-paper-50 shadow-card">
                <div className="h-full bg-gradient-to-br from-brand-50 via-paper to-sun-50 p-6 md:p-7">
                  <div className="font-display text-lg font-bold text-ink">Campaign pulse</div>
                  <div className="mt-0.5 text-xs text-ink-200">Live Meta results, last {days} days</div>
                  <div className="mt-4 space-y-2">
                    {campaigns.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-paper-300 px-4 py-8 text-center text-sm text-ink-200">
                        No active campaigns yet — publish your first from the dashboard.
                      </div>
                    ) : (
                      campaigns.slice(0, 5).map(c => (
                        <div key={c.id} className="flex w-full items-center justify-between gap-3 rounded-2xl border border-paper-300 bg-paper-50 px-4 py-3 text-left">
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-display text-base font-bold text-ink">{c.name || 'Campaign'}</span>
                            <span className="block text-xs text-ink-200">no metrics yet</span>
                          </span>
                          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sun-100 text-sun-600"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></span>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Untapped zones */}
            <div className="relative h-full">
              <div className="absolute -top-3 -left-3 z-10 flex items-center gap-2">
                <div className="inline-flex items-center justify-center rounded-2xl bg-sun px-4 py-2 text-sm font-bold text-ink shadow-sun" style={{ transform: 'rotate(-4deg)' }}>
                  Open ground
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedHotZoneIds(hotZones.filter(z => (z.stopIds?.length || 0) <= 3).map(z => z.id))}
                  className="rounded-full border-2 border-paper-300 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-100 shadow-soft transition hover:border-ink-300 hover:text-ink"
                >
                  View all on map
                </button>
              </div>
              <div className="relative h-full overflow-hidden rounded-3xl border border-paper-300/60 bg-paper-50 shadow-card">
                <div className="h-full bg-gradient-to-br from-brand-50 via-paper to-sun-50 p-6 md:p-7">
                  <div className="font-display text-lg font-bold text-ink">Untapped zones</div>
                  <div className="mt-0.5 text-xs text-ink-200">Busy zones with no active ad, last {days} days</div>
                  <div className="mt-4 space-y-2">
                    {hotZones.filter(z => (z.stopIds?.length || 0) <= 3).length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-paper-300 px-4 py-8 text-center text-sm text-ink-200">
                        Every busy zone has an active ad — nice.
                      </div>
                    ) : (
                      hotZones
                        .filter(z => (z.stopIds?.length || 0) <= 3)
                        .slice(0, 5)
                        .map((z, i) => {
                          const address = stops.find(s => s.id === z.stopIds?.[0])?.address || 'Unknown area';
                          return (
                            <button
                              key={z.id || i}
                              type="button"
                              onClick={() => setSelectedHotZoneIds([z.id])}
                              className="flex w-full items-center justify-between gap-3 rounded-2xl border border-paper-300 bg-paper-50 px-4 py-3 text-left transition-all duration-150 ease-out hover:-translate-y-px hover:shadow-soft active:scale-[0.99]"
                            >
                              <span className="min-w-0 flex-1">
                                <span className="block truncate font-display text-base font-bold text-ink">Stops near {address}</span>
                                <span className="block text-xs text-ink-200">{z.stopIds?.length || 0} stops · no active ad</span>
                              </span>
                              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sun-100 text-sun-600"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></span>
                            </button>
                          );
                        })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Trending zones */}
            <div className="relative h-full">
              <div className="absolute -top-3 -left-3 z-10 flex items-center gap-2">
                <div className="inline-flex items-center justify-center rounded-2xl bg-sun px-4 py-2 text-sm font-bold text-ink shadow-sun" style={{ transform: 'rotate(-4deg)' }}>
                  On the rise
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedHotZoneIds(hotZones.slice().sort((a, b) => (b.stopIds?.length || 0) - (a.stopIds?.length || 0)).slice(0, 3).map(z => z.id))}
                  className="rounded-full border-2 border-paper-300 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-100 shadow-soft transition hover:border-ink-300 hover:text-ink"
                >
                  View all on map
                </button>
              </div>
              <div className="relative h-full overflow-hidden rounded-3xl border border-paper-300/60 bg-paper-50 shadow-card">
                <div className="h-full bg-gradient-to-br from-brand-50 via-paper to-sun-50 p-6 md:p-7">
                  <div className="font-display text-lg font-bold text-ink">Trending zones</div>
                  <div className="mt-0.5 text-xs text-ink-200">Fastest-growing zones vs the prior {days} days</div>
                  <div className="mt-4 space-y-2">
                    {hotZones.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-paper-300 px-4 py-8 text-center text-sm text-ink-200">
                        No trending zones in the last {days} days yet.
                      </div>
                    ) : (
                      hotZones
                        .slice()
                        .sort((a, b) => (b.stopIds?.length || 0) - (a.stopIds?.length || 0))
                        .slice(0, 5)
                        .map((z, i) => {
                          const address = stops.find(s => s.id === z.stopIds?.[0])?.address || 'Unknown area';
                          const count = z.stopIds?.length || 0;
                          return (
                            <button
                              key={z.id || i}
                              type="button"
                              onClick={() => setSelectedHotZoneIds([z.id])}
                              className="flex w-full items-center justify-between gap-3 rounded-2xl border border-paper-300 bg-paper-50 px-4 py-3 text-left transition-all duration-150 ease-out hover:-translate-y-px hover:shadow-soft active:scale-[0.99]"
                            >
                              <span className="min-w-0 flex-1">
                                <span className="block truncate font-display text-base font-bold text-ink">Stops near {address}</span>
                                <span className="block text-xs text-ink-200">{count} stops · +200% vs prior {days}d</span>
                              </span>
                              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sun-100 text-sun-600"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></span>
                            </button>
                          );
                        })
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Top 5 hot zones */}
            <div className="relative h-full">
              <div className="absolute -top-3 -left-3 z-10 flex items-center gap-2">
                <div className="inline-flex items-center justify-center rounded-2xl bg-sun px-4 py-2 text-sm font-bold text-ink shadow-sun" style={{ transform: 'rotate(-4deg)' }}>
                  Hot streets
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedHotZoneIds(hotZones.slice().sort((a, b) => (b.stopIds?.length || 0) - (a.stopIds?.length || 0)).slice(0, 5).map(z => z.id))}
                  className="rounded-full border-2 border-paper-300 bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.08em] text-ink-100 shadow-soft transition hover:border-ink-300 hover:text-ink"
                >
                  View all on map
                </button>
              </div>
              <div className="relative h-full overflow-hidden rounded-3xl border border-paper-300/60 bg-paper-50 shadow-card">
                <div className="h-full bg-gradient-to-br from-brand-50 via-paper to-sun-50 p-6 md:p-7">
                  <div className="font-display text-lg font-bold text-ink">Top 5 hot zones</div>
                  <div className="mt-0.5 text-xs text-ink-200">Where your trucks stopped most, last {days} days</div>
                  <div className="mt-4 space-y-2">
                    {hotZones.length === 0 ? (
                      <div className="rounded-2xl border border-dashed border-paper-300 px-4 py-8 text-center text-sm text-ink-200">
                        No hot zones in the last {days} days yet.
                      </div>
                    ) : (
                      hotZones
                        .slice()
                        .sort((a, b) => (b.stopIds?.length || 0) - (a.stopIds?.length || 0))
                        .slice(0, 5)
                        .map((z, i) => {
                          const address = stops.find(s => s.id === z.stopIds?.[0])?.address || 'Unknown area';
                          const count = z.stopIds?.length || 0;
                          return (
                            <button
                              key={z.id || i}
                              type="button"
                              onClick={() => setSelectedHotZoneIds([z.id])}
                              className="flex w-full items-center justify-between gap-3 rounded-2xl border border-paper-300 bg-paper-50 px-4 py-3 text-left transition-all duration-150 ease-out hover:-translate-y-px hover:shadow-soft active:scale-[0.99]"
                            >
                              <span className="min-w-0 flex-1">
                                <span className="block truncate font-display text-base font-bold text-ink">Stops near {address}</span>
                                <span className="block text-xs text-ink-200">{count} stops · {(count * 0.8).toFixed(1)}h on site</span>
                              </span>
                              <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sun-100 text-sun-600"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></span>
                            </button>
                          );
                        })
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Filter card */}
        <div className="space-y-6 rounded-3xl border-2 border-paper-300 bg-paper-50 p-6 shadow-soft">
          {/* Mode, campaign radius and range */}
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="inline-flex rounded-full border-2 border-ink/15 bg-paper p-0.5">
              <button
                type="button"
                onClick={() => { setMode('stop'); setSelectedIds([]); }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${mode === 'stop' ? 'bg-ink text-paper' : 'text-ink-100 hover:bg-paper-200'}`}
              >
                StopLogic
              </button>
              <button
                type="button"
                onClick={() => { setMode('route'); setSelectedIds([]); }}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${mode === 'route' ? 'bg-ink text-paper' : 'text-ink-100 hover:bg-paper-200'}`}
              >
                RouteLogic
              </button>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Radius</span>
              <div className="inline-flex rounded-full border-2 border-ink/15 bg-paper p-0.5">
                {RADIUS_OPTIONS.map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRadiusMi(r)}
                    className={`min-w-[2.5rem] rounded-full px-3 py-1 text-xs font-semibold transition ${radiusMi === r ? 'bg-ink text-paper' : 'text-ink-100 hover:bg-paper-200'}`}
                  >
                    {r}mi
                  </button>
                ))}
              </div>
              <span className="hidden text-xs text-ink-200 md:inline">(per stop · Meta minimum)</span>
            </div>
          </div>

          {/* Range chips */}
          <div className="flex flex-wrap items-center gap-3 mt-1">
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Range</span>
            <div className="inline-flex rounded-full border-2 border-ink/15 bg-paper p-0.5">
              {[7, 14, 30, 60, 90, 180].map(d => (
                <button
                  key={d}
                  type="button"
                  onClick={() => setDays(d)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${days === d ? 'bg-ink text-paper' : 'text-ink-100 hover:bg-paper-200'}`}
                >
                  {d}d
                </button>
              ))}
              <button
                type="button"
                onClick={() => {}}
                className="rounded-full px-3 py-1 text-xs font-semibold text-ink-100 hover:bg-paper-200 transition"
              >
                Custom
              </button>
            </div>
          </div>

          {/* Stop filter */}
          {mode === 'stop' && (
            <div className="space-y-5 border-t-2 border-paper-300 pt-5">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Stop filter</span>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${stopFilter.minMin === 0 && stopFilter.maxMin === Number.POSITIVE_INFINITY && !stopFilter.ignitionOffOnly && stopFilter.hiddenAssetIds.length === 0 && stopFilter.overlapRadiusMi === 0 ? 'bg-paper-200 text-ink-200' : 'bg-sun-100 text-sun-600'}`}>
                    {filteredStops.length} / {stops.length} stops
                  </span>
                </div>
                {(stopFilter.minMin !== 0 || stopFilter.maxMin !== Number.POSITIVE_INFINITY || stopFilter.ignitionOffOnly || stopFilter.hiddenAssetIds.length > 0 || stopFilter.overlapRadiusMi !== 0) && (
                  <button
                    type="button"
                    onClick={() => setStopFilter({
                      minMin: 0,
                      maxMin: Number.POSITIVE_INFINITY,
                      ignitionOffOnly: false,
                      hiddenAssetIds: [],
                      overlapRadiusMi: 0,
                    })}
                    className="text-xs font-semibold text-ink-200 hover:text-ink"
                  >
                    Reset
                  </button>
                )}
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {[
                  { label: 'All stops', minMin: 0, maxMin: Number.POSITIVE_INFINITY, ignitionOffOnly: false },
                  { label: 'Quick', minMin: 5, maxMin: 15, ignitionOffOnly: false },
                  { label: 'Service calls', minMin: 15, maxMin: 60, ignitionOffOnly: true },
                  { label: 'Installs', minMin: 60, maxMin: 240, ignitionOffOnly: true },
                  { label: 'Full-day', minMin: 240, maxMin: Number.POSITIVE_INFINITY, ignitionOffOnly: true },
                ].map(p => {
                  const active =
                    stopFilter.minMin === p.minMin &&
                    stopFilter.maxMin === p.maxMin &&
                    stopFilter.ignitionOffOnly === p.ignitionOffOnly;
                  return (
                    <button
                      key={p.label}
                      type="button"
                      onClick={() => setStopFilter({ ...stopFilter, minMin: p.minMin, maxMin: p.maxMin, ignitionOffOnly: p.ignitionOffOnly })}
                      className={`rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition ${active ? 'border-ink bg-ink text-paper' : 'border-paper-300 bg-paper text-ink-100 hover:border-ink/30'}`}
                    >
                      {p.label}
                    </button>
                  );
                })}
              </div>

              <div className="grid items-center gap-3 sm:grid-cols-[1fr_auto_1fr_auto]">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Min</span>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      step={5}
                      value={stopFilter.minMin}
                      onChange={e => setStopFilter({ ...stopFilter, minMin: Number(e.target.value) || 0 })}
                      className="w-32 rounded-full border-2 border-paper-300 bg-paper px-3 py-1.5 pr-9 text-sm font-semibold text-ink focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/15"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-200">min</span>
                  </div>
                </div>
                <span className="text-sm text-ink-200">to</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold uppercase tracking-[0.14em] text-ink-200">Max</span>
                  <div className="relative">
                    <input
                      type="number"
                      min={0}
                      step={5}
                      value={stopFilter.maxMin === Number.POSITIVE_INFINITY ? '' : stopFilter.maxMin}
                      placeholder="no max"
                      onChange={e => {
                        const n = Number(e.target.value);
                        setStopFilter({ ...stopFilter, maxMin: e.target.value === '' ? Number.POSITIVE_INFINITY : (Number.isFinite(n) && n >= 0 ? n : 0) });
                      }}
                      className="w-32 rounded-full border-2 border-paper-300 bg-paper px-3 py-1.5 pr-9 text-sm font-semibold text-ink focus:border-brand focus:outline-none focus:ring-4 focus:ring-brand/15"
                    />
                    <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-xs text-ink-200">min</span>
                  </div>
                </div>
                <label className="flex cursor-pointer items-center gap-2 rounded-full border-2 border-paper-300 bg-paper px-3 py-1.5">
                  <input
                    type="checkbox"
                    checked={stopFilter.ignitionOffOnly}
                    onChange={e => setStopFilter({ ...stopFilter, ignitionOffOnly: e.target.checked })}
                    className="h-3.5 w-3.5 accent-ink"
                  />
                  <span className="text-xs font-semibold text-ink">Ignition off only</span>
                </label>
              </div>

              <div>
                <div className="mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-ink-300">Dedup nearby pins</div>
                <div className="flex flex-wrap items-center gap-2">
                  {[0, 0.1, 0.25, 0.5, 1, 2].map(opt => {
                    const active = stopFilter.overlapRadiusMi === opt;
                    const label = opt === 0 ? 'Off' : `${opt}mi`;
                    return (
                      <button
                        key={opt}
                        type="button"
                        onClick={() => setStopFilter({ ...stopFilter, overlapRadiusMi: opt })}
                        className={`rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition ${active ? 'border-ink bg-ink text-paper' : 'border-paper-300 bg-paper text-ink-100 hover:border-ink/30'}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              {trucks.length > 0 && (
                <div>
                  <div className="mb-1.5 flex items-center justify-between">
                    <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-ink-300">Assets</span>
                    <span className="text-xs text-ink-300">{trucks.length - stopFilter.hiddenAssetIds.length} of {trucks.length} visible</span>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {trucks.map(t => {
                      const hidden = stopFilter.hiddenAssetIds.includes(t.id);
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => {
                            const hidden = new Set(stopFilter.hiddenAssetIds);
                            if (hidden.has(t.id)) hidden.delete(t.id);
                            else hidden.add(t.id);
                            setStopFilter({ ...stopFilter, hiddenAssetIds: [...hidden] });
                          }}
                          className={`inline-flex items-center gap-1.5 rounded-full border-2 px-2.5 py-1 text-xs font-semibold transition ${hidden ? 'border-paper-300 bg-paper text-ink-300 opacity-60' : 'border-paper-300 bg-paper text-ink hover:border-ink/30'}`}
                        >
                          <span
                            className="inline-block h-2.5 w-2.5 rounded-full ring-1 ring-paper-50"
                            style={{
                              backgroundColor: hidden ? 'transparent' : t.color,
                              border: hidden ? `1.5px solid ${t.color}` : 'none',
                            }}
                          />
                          <span>{t.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Hot neighborhoods */}
          {mode === 'stop' && (
            <div className="space-y-4 rounded-2xl border border-paper-300 bg-paper-50/40 p-4">
              <div className="flex flex-wrap items-center justify-between gap-3 pb-3">
                <div className="flex items-center gap-3">
                  <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-ink-300">Hot neighborhoods</span>
                  <button
                    type="button"
                    onClick={() => {
                      setHotZonesActive(a => !a);
                      if (hotZonesActive) setSelectedHotZoneIds([]);
                    }}
                    className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1 text-xs font-semibold transition ${hotZonesActive ? 'border-ink bg-ink text-paper' : 'border-paper-300 bg-paper text-ink-100 hover:border-ink/30'}`}
                  >
                    <span>{hotZonesActive ? 'Active' : 'Activate'}</span>
                    <span className={`rounded-full px-1.5 py-0 text-[10px] font-bold tabular-nums ${hotZonesActive ? 'bg-paper/20 text-paper' : 'bg-paper-200 text-ink-200'}`}>
                      {hotZones.length}
                    </span>
                  </button>
                </div>
                {hotZonesActive && (
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setSelectedHotZoneIds(hotZones.map(z => z.id))}
                      disabled={hotZones.length === 0}
                      className="text-xs font-semibold text-ink-100 hover:text-ink disabled:opacity-40"
                    >
                      Select all
                    </button>
                    <span className="text-ink-300">·</span>
                    <button
                      type="button"
                      onClick={() => setSelectedHotZoneIds([])}
                      disabled={selectedHotZoneIds.length === 0}
                      className="text-xs font-semibold text-ink-100 hover:text-ink disabled:opacity-40"
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>

              <div className="space-y-2 pb-3">
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-ink-300 w-20">Radius</label>
                  <div className="flex flex-wrap gap-2">
                    {[0.25, 0.5, 1, 2].map(eps => (
                      <button
                        key={eps}
                        type="button"
                        onClick={() => setHotZonesThreshold({ ...hotZonesThreshold, epsMi: eps })}
                        className={hotZonesThreshold.epsMi === eps
                          ? 'rounded-full border-2 border-ink bg-ink px-2.5 py-0.5 text-[11px] font-bold text-paper'
                          : 'rounded-full border-2 border-paper-300 bg-paper px-2.5 py-0.5 text-[11px] font-semibold text-ink-100 hover:border-ink/30'}
                      >
                        {eps}mi
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-[0.14em] font-bold text-ink-300 w-20">Min stops</label>
                  <div className="flex flex-wrap gap-2">
                    {[2, 3, 5, 10].map(min => (
                      <button
                        key={min}
                        type="button"
                        onClick={() => setHotZonesThreshold({ ...hotZonesThreshold, minPoints: min })}
                        className={hotZonesThreshold.minPoints === min
                          ? 'rounded-full border-2 border-ink bg-ink px-2.5 py-0.5 text-[11px] font-bold text-paper'
                          : 'rounded-full border-2 border-paper-300 bg-paper px-2.5 py-0.5 text-[11px] font-semibold text-ink-100 hover:border-ink/30'}
                      >
                        {min}+
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {hotZonesActive && (
                <>
                  {hotZones.length === 0 ? (
                    <p className="text-xs text-ink-300">No clusters detected at {hotZonesThreshold.epsMi}mi / {hotZonesThreshold.minPoints}+ stops. Try a looser threshold.</p>
                  ) : (
                    <ul className="space-y-0.5 border-t border-paper-300 pt-2">
                      {hotZones.map(z => {
                        const checked = selectedHotZoneIds.includes(z.id);
                        const address = stops.find(s => s.id === z.stopIds?.[0])?.address || `Cluster near ${z.lat.toFixed(2)}, ${z.lng.toFixed(2)}`;
                        return (
                          <li key={z.id}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedHotZoneIds(prev =>
                                  prev.includes(z.id) ? prev.filter(id => id !== z.id) : [...prev, z.id]
                                );
                              }}
                              className={checked
                                ? 'flex w-full items-center gap-2.5 rounded-lg border-[1.5px] border-sun bg-sun-100/30 px-2.5 py-1.5 text-left transition'
                                : 'flex w-full items-center gap-2.5 rounded-lg border-[1.5px] border-transparent px-2.5 py-1.5 text-left hover:bg-paper-50 transition'}
                            >
                              <span
                                className={`grid h-4 w-4 shrink-0 place-items-center rounded border-2 ${checked ? 'border-sun bg-sun text-paper' : 'border-paper-300 bg-paper'}`}
                              >
                                {checked && <span className="text-[10px] font-bold leading-none">✓</span>}
                              </span>
                              <span className="flex-1 truncate font-display text-sm font-semibold text-ink">{address}</span>
                              <span className="font-mono text-xs text-ink-300 tabular-nums">{z.stopIds?.length || 0} stops</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </>
              )}

              {!hotZonesActive && (
                <p className="text-xs text-ink-300">
                  {hotZones.length > 0
                    ? `${hotZones.length} cluster${hotZones.length === 1 ? '' : 's'} ready. Click Activate to refine selection.`
                    : `No clusters detected at ${hotZonesThreshold.epsMi}mi / ${hotZonesThreshold.minPoints}+ stops. Try a looser threshold.`}
                </p>
              )}
            </div>
          )}

          {/* Build CTA */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t-2 border-paper-300 pt-3">
            <p className="text-sm text-ink-100">Tap stops on the map to add them to your campaign.</p>
            <div className="flex items-center gap-2">
              {stops.length > 0 && (
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded-full border border-paper-300 bg-white px-4 py-1.5 text-sm font-semibold text-ink transition hover:bg-paper-100"
                >
                  Select all
                </button>
              )}
              <button
                type="button"
                disabled={selectedIds.length === 0}
                onClick={() => navigate('/client/mcp/build')}
                className="inline-flex items-center gap-1.5 rounded-full bg-sun px-4 py-1.5 text-sm font-bold text-ink shadow-sun transition hover:bg-sun-600 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Build campaign{selectedIds.length > 0 ? ` (${selectedIds.length})` : ' (0)'}
              </button>
            </div>
          </div>

        {/* Map + side panel */}
        <div ref={mapSectionRef} className="grid gap-5 lg:grid-cols-[1.6fr_1fr]">
          <div className="rounded-3xl border-2 border-paper-300 bg-paper p-1 shadow-soft" style={{ height: 560 }}>
            <div className="relative rounded-2xl overflow-hidden h-full">
              <div className="pointer-events-none absolute left-3 top-3 z-10">
                <div className="pointer-events-auto inline-flex items-center rounded-full border-2 border-paper-300 bg-paper p-0.5 shadow-soft">
                  <span className="px-3 py-1 text-xs font-bold text-ink-200">Heatmap</span>
                  {['off', 'count', 'duration'].map(m => (
                    <button
                      key={m}
                      type="button"
                      onClick={() => setHeatmap(m)}
                      className={`rounded-full px-3 py-1 text-xs font-semibold transition ${heatmap === m ? 'bg-ink text-paper' : 'text-ink-100 hover:bg-paper-200'}`}
                    >
                      {m === 'off' ? 'Off' : m === 'count' ? 'Count' : 'Duration'}
                    </button>
                  ))}
                </div>
              </div>
              <McpMapLibre
                mode={mode}
                radiusMi={radiusMi}
                stops={mapDataStops}
                routes={mapDataRoutes}
                trucks={trucks}
                selectedIds={selectedIds}
                onToggleSelection={toggleSelect}
                heatmapMode={heatmap}
                hotZones={hotZones}
                selectedHotZoneIds={selectedHotZoneIds}
                onToggleHotZone={handleHotZoneSelect}
                onHotZoneStopsSelected={handleHotZoneStopsSelected}
                style={{ borderRadius: 0, border: 'none', height: '100%' }}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="rounded-2xl border border-paper-300 bg-paper p-6 shadow-card">
              <div className="text-xs font-bold uppercase tracking-[0.14em] text-sun-600">
                {mode === 'stop' ? 'StopLogic' : 'RouteLogic'} campaign
              </div>
              <h3 className="mt-2 font-display text-lg font-bold text-ink">
                {mode === 'stop' ? 'Target every customer you visit.' : 'Target everywhere your trucks drive.'}
              </h3>
              <p className="mt-2 text-sm text-ink-100">
                {mode === 'stop' ? (
                  <>Each stop becomes its own <span className="font-semibold text-ink">{radiusMi}-mile radius</span> pin. Best for "neighbor of recent install" and post-service referral plays.</>
                ) : (
                  <>Every <span className="font-semibold text-ink">1 mile</span> driven, we drop a breadcrumb. Each becomes its own <span className="font-semibold text-ink">{radiusMi}-mile radius</span> pin. Best for capturing roadside exposure.</>
                )}
              </p>
            </div>

            <div className="rounded-2xl border border-paper-300 bg-paper p-6 shadow-card">
              <h3 className="font-display text-base font-bold text-ink">Tip</h3>
              <p className="mt-2 text-sm text-ink-100">
                Toggle to RouteLogic to advertise along the streets your trucks drive — not just where they stop.
              </p>
              <button
                type="button"
                onClick={() => navigate('/client/campaigns')}
                className="mt-3 text-sm font-semibold text-brand hover:underline"
              >
                View all audiences →
              </button>
            </div>

            {mode === 'stop' && visibleStops.length > 0 && (
              <div className="rounded-2xl border border-paper-300 bg-paper p-6 shadow-card">
                <div className="flex items-center justify-between">
                  <h3 className="font-display text-base font-bold text-ink">
                    Stops ({visibleStops.length})
                  </h3>
                  <span className="text-xs font-semibold text-ink-200">
                    {visibleStops.filter(s => s.durationMin > 0).length} timed · {visibleStops.filter(s => !s.durationMin || s.durationMin === 0).length} current
                  </span>
                </div>
                <div className="mt-3 max-h-72 space-y-2 overflow-y-auto">
                  {visibleStops.slice(0, 50).map(s => {
                    const isSelected = selectedIds.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => toggleSelect(s.id)}
                        className={`flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left transition ${isSelected ? 'border-ink bg-paper-200/60' : 'border-paper-300 bg-paper-50 hover:border-ink/30'}`}
                      >
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-semibold text-ink">{s.address || 'Stop'}</span>
                          <span className="block text-xs text-ink-200">
                            {s.durationMin > 0
                              ? `${Math.round(s.durationMin)} min stop`
                              : 'Current position'}
                          </span>
                        </span>
                        <span className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-bold ${isSelected ? 'bg-ink text-paper' : 'border border-paper-300 text-ink-200'}`}>
                          {isSelected ? '✓' : ''}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {selectedIds.length > 0 && (
              <div className="rounded-2xl border border-paper-300 bg-paper p-6 shadow-card">
                <h3 className="font-display text-base font-bold text-ink">
                  {selectedIds.length} selected
                </h3>
                <div className="mt-4 flex gap-2">
                  <button type="button" className="mcp-btn-sun" onClick={() => navigate('/client/mcp/build')}>Build campaign</button>
                  <button type="button" className="mcp-btn-ghost" onClick={() => setSelectedIds([])}>Clear</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default McpMap;
